import { useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, ChevronDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { ExploreFilters } from '@/components/explore/ExploreFilters';
import { CompanyCard } from '@/components/cards/Cards';
import { SectionHeader, EmptyState, DemoBadge } from '@/components/common/Primitives';
import { useData } from '@/hooks/useData';
import { VisitorOnly } from '@/hooks/useIsVisitor';
import { useSeo } from '@/lib/seo';
import { filterCompanies, sortCompanies, paramsToFilters, filtersToParams } from '@/lib/filters';
import { stateName } from '@/data/states';

const PAGE = 12;

export default function Companies() {
  const [sp, setSp] = useSearchParams();
  const filters = paramsToFilters(sp);
  const [limit, setLimit] = useState(PAGE);
  const { publicCompanies, categoryById } = useData();
  useSeo({ title: filters.uf ? `Empresas em ${stateName(filters.uf)}` : 'Empresas', description: 'Diretório de empresas da rede Brasil Livre Business.' });

  const list = useMemo(() => sortCompanies(filterCompanies(publicCompanies, filters, categoryById)), [publicCompanies, sp.toString(), categoryById]); // eslint-disable-line react-hooks/exhaustive-deps
  const update = (f) => { setSp(filtersToParams(f), { replace: true }); setLimit(PAGE); };

  return (
    <div className="container-x py-10 sm:py-14">
      <SectionHeader
        eyebrow="Diretório nacional"
        title="Empresas"
        text="Encontre fornecedores, prestadores de serviço e parceiros em todo o Brasil."
        right={<VisitorOnly><DemoBadge label="Empresas fictícias" /></VisitorOnly>}
      />
      <ExploreFilters filters={filters} onChange={update} onClear={() => update({})} resultCount={list.length} testPrefix="companies-filters" />
      <div className="mt-6">
        {list.length ? (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="companies-grid">
              {list.slice(0, limit).map((c) => <CompanyCard key={c.id} company={c} />)}
            </div>
            {limit < list.length && (
              <div className="mt-8 flex justify-center">
                <Button variant="secondary" className="border border-white/10" onClick={() => setLimit((l) => l + PAGE)} data-testid="companies-load-more">
                  <ChevronDown /> Carregar mais ({list.length - limit})
                </Button>
              </div>
            )}
          </>
        ) : (
          <EmptyState icon={Building2} title="Nenhuma empresa encontrada" text="Não há empresas com essa combinação de filtros. Remova algum filtro ou cadastre a sua." action="Limpar filtros" onAction={() => update({})} testId="companies-empty" />
        )}
      </div>
    </div>
  );
}